export const EMPTY_ADDRESS_COMPONENTS = {
  street: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
};

/**
 * Compose structured address components into a single multi-line string.
 *
 * Renders up to three lines, skipping empty parts:
 *   <street>
 *   <street2>
 *   <city>, <state> <zip>
 *
 * Mirrors the backend `compose_address` used by the `virtualize_address` flag so the
 * client-side preview matches what the service delivers.
 */
export const composeAddress = (components, separator = "\n") => {
  if (!components) return "";

  const part = (key) => (components[key] ?? "").toString().trim();

  const street = part("street");
  const street2 = part("street2");
  const city = part("city");
  const state = part("state");
  const zip = part("zip");

  const cityState = [city, state].filter(Boolean).join(", ");
  const locality = [cityState, zip].filter(Boolean).join(" ");

  return [street, street2, locality].filter(Boolean).join(separator);
};

/**
 * Parse a flat single-line (or multi-line) address into structured components.
 *
 * Shared with the insured-address input (#7541) so people/brokers and insureds seed
 * legacy flat strings the same way. Best-effort only: it is a heuristic, and
 * `composeAddress(parseAddressComponents(s))` is NOT guaranteed to equal `s`, so
 * callers must not overwrite a stored flat address with a re-composition of a parse.
 */
export const parseAddressComponents = (address) => {
  if (!address) return { ...EMPTY_ADDRESS_COMPONENTS };

  const tokens = address
    .split(/[\n,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  let zip = "";
  let state = "";

  // Scan last 3 tokens for postal code (avoids matching street numbers in first tokens)
  const scanFrom = Math.max(0, tokens.length - 3);
  const lastIndex = tokens.length - 1;
  let zipIndex = -1;
  for (let i = lastIndex; i >= scanFrom && !zip; i -= 1) {
    const token = tokens[i];
    let match;
    // "NY 10118" — 2-3 letter code + numeric postal
    if ((match = token.match(/^([A-Z]{2,3})\s+(\d{4,6}(?:-\d{3,4})?)$/))) {
      state = match[1];
      zip = match[2];
      tokens.splice(i, 1);
      // "10117 Berlin" — numeric postal + single-word city
    } else if ((match = token.match(/^(\d{4,6}(?:-\d{3,4})?)\s+(\S+)$/))) {
      zip = match[1];
      tokens[i] = match[2];
      // "10118" — pure numeric postal
    } else if ((match = token.match(/^(\d{4,6}(?:-\d{3,4})?)$/))) {
      zip = match[1];
      tokens.splice(i, 1);
      // "SW1A 2AA" or "K1A 0B1" — alphanumeric postal (must contain digits)
    } else if (
      (match = token.match(/^([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/i))
    ) {
      zip = match[1];
      tokens.splice(i, 1);
    }
    if (zip) zipIndex = i;
  }

  // If no state yet, check if last token is a short region/state code
  if (
    !state &&
    tokens.length > 0 &&
    /^[A-Z]{2,3}$/.test(tokens[tokens.length - 1])
  ) {
    state = tokens.pop();
  }

  // If something came after the postcode ("..., NY 10118, USA") and 3+ tokens remain, the
  // last no-digit token is likely a country — drop it. When the postcode was the last
  // token, the token before it is the city ("..., Suite 1400, Dallas, TX 75201"), not a country.
  if (
    zip &&
    zipIndex < lastIndex &&
    tokens.length >= 3 &&
    !/\d/.test(tokens[tokens.length - 1])
  ) {
    tokens.pop();
  }

  let street = "";
  let city = "";
  if (tokens.length > 1) {
    city = tokens[tokens.length - 1];
    street = tokens.slice(0, tokens.length - 1).join(", ");
  } else if (tokens.length === 1) {
    if (zip) {
      city = tokens[0];
    } else {
      street = tokens[0];
    }
  }

  return { street, street2: "", city, state, zip };
};

/**
 * True when two component objects hold the same values, comparing trimmed strings and
 * treating missing/null/undefined keys as empty.
 */
export const addressComponentsEqual = (a, b) => {
  const value = (components, key) => ((components?.[key] ?? "") + "").trim();
  return Object.keys(EMPTY_ADDRESS_COMPONENTS).every(
    (key) => value(a, key) === value(b, key)
  );
};

/** Every component trimmed, with missing keys filled in as "". */
export const trimAddressComponents = (components) =>
  Object.fromEntries(
    Object.keys(EMPTY_ADDRESS_COMPONENTS).map((key) => [
      key,
      ((components?.[key] ?? "") + "").trim(),
    ])
  );

/** True when at least one component holds a non-empty value. */
export const hasAddressComponents = (components) =>
  Object.values(trimAddressComponents(components)).some(Boolean);
